/* =====================================================================
 * CUSTOM:AQARA-HK  Product-page enhancements
 * ---------------------------------------------------------------------
 * Two independent, self-contained enhancements (each a guarded IIFE that
 * silently no-ops if its target markup is absent, no dependencies):
 *
 *   1) Sticky add-to-cart bar colour variant selector — mirrors the
 *      product page's colour selector into the sticky (drawer) buy-bar as
 *      small TEXT pills (e.g. 白 / 灰). Clicking a pill proxies the click
 *      to the REAL option input in the main form, so the theme handles
 *      the variant change (price, media, URL). Two-way active sync.
 *
 *   2) Delivery-ETA line under the main add-to-cart button — shows
 *      "預計 2–3 個工作天內送達" below the inline buy button, but ONLY when
 *      the variant is in stock (the theme disables / relabels the button
 *      when sold out). Re-evaluated on every variant change.
 * ===================================================================== */
(function () {
  'use strict';

  var STYLE_ID = 'aqara-sticky-variants-style';
  var WRAP_CLASS = 'product-sticky-bar__variants';
  var BTN_CLASS = 'product-sticky-bar__variant';
  var COLOR_RE = /(顏色|颜色|colou?r|色|尺寸|尺碼|大小|size)/i;
  var MAX_PILL_LEN = 24; /* skip options whose values are long sentences (e.g. install-service text) */

  function injectStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var css = [
      '.' + WRAP_CLASS + '{display:flex;align-items:center;gap:8px;margin:0 0 0 auto;flex:0 1 auto;flex-wrap:wrap;justify-content:flex-end;}',
      '.' + BTN_CLASS + '{padding:7px 16px;border:1px solid #d5d5d5;border-radius:100px;background:#fff;',
        'color:#333;font-size:14px;line-height:1.1;cursor:pointer;white-space:nowrap;flex:0 0 auto;',
        'transition:border-color .15s ease,background-color .15s ease,color .15s ease;}',
      '.' + BTN_CLASS + ':hover{border-color:#9aa9bd;}',
      '.' + BTN_CLASS + '.is-active{border-color:rgba(78,110,147,.75);background-color:rgba(78,110,147,.12);color:#23364d;font-weight:600;}',
      '.' + BTN_CLASS + ':focus-visible{outline:2px solid #4e6e93;outline-offset:2px;}',
      '@media (max-width:749px){.' + WRAP_CLASS + '{gap:6px;margin:0 0 0 auto;}.' + BTN_CLASS + '{padding:6px 12px;font-size:13px;}}'
    ].join('');
    var s = document.createElement('style');
    s.id = STYLE_ID;
    s.textContent = css;
    document.head.appendChild(s);
  }

  function getDrawer() {
    return document.querySelector('.product-buy-buttons--drawer');
  }

  function getOptionsRoot() {
    return document.querySelector('product-options-element[data-main-product="true"]')
        || document.querySelector('product-options-element.product-options--root')
        || document.querySelector('.product-options--root');
  }

  /* An option is pill-worthy only if it has >= 2 values AND every value is
     short (a real colour/size choice, not a long install-service sentence). */
  function optionIsShortMultiValue(optionBlock) {
    var inputs = getInputs(optionBlock);
    if (inputs.length < 2) return false;
    for (var i = 0; i < inputs.length; i++) {
      if ((inputs[i].value || '').length > MAX_PILL_LEN) return false;
    }
    return true;
  }

  /* Choose the option to mirror: among the short multi-value options, prefer a
     colour/size-named one; otherwise the first. Returns null when none qualify
     (e.g. single-colour products, or products whose only multi-value option is
     long-text like an installation service) so nothing is shown. */
  function getTargetOption() {
    var root = getOptionsRoot();
    if (!root) return null;
    var opts = Array.prototype.slice.call(root.querySelectorAll('.product-options--option'));
    var candidates = [];
    for (var i = 0; i < opts.length; i++) {
      if (optionIsShortMultiValue(opts[i])) candidates.push(opts[i]);
    }
    if (!candidates.length) return null;
    for (var j = 0; j < candidates.length; j++) {
      var nameEl = candidates[j].querySelector('.swatches--name, .product-options--option-name, label');
      if (nameEl && COLOR_RE.test(nameEl.textContent || '')) return candidates[j];
    }
    return candidates[0];
  }

  function getInputs(optionBlock) {
    return Array.prototype.slice.call(
      optionBlock.querySelectorAll('input.swatch--input, input[type="radio"]')
    );
  }

  function currentValue(optionBlock) {
    var c = optionBlock.querySelector('input.swatch--input:checked, input[type="radio"]:checked');
    return c ? c.value : null;
  }

  function build() {
    var drawer = getDrawer();
    var optionBlock = getTargetOption();
    if (!drawer || !optionBlock) return;
    var inputs = getInputs(optionBlock);
    if (inputs.length < 2) return; /* nothing to choose */

    var old = drawer.querySelector('.' + WRAP_CLASS);
    if (old) old.parentNode.removeChild(old);

    var active = currentValue(optionBlock);
    var wrap = document.createElement('div');
    wrap.className = WRAP_CLASS;

    inputs.forEach(function (inp) {
      var val = inp.value;
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = BTN_CLASS;
      btn.setAttribute('data-value', val);
      btn.textContent = val;
      if (val === active) btn.classList.add('is-active');

      btn.addEventListener('click', function () {
        var block = getTargetOption();
        if (!block) return;
        var list = getInputs(block), real = null;
        for (var i = 0; i < list.length; i++) {
          if (list[i].value === val) { real = list[i]; break; }
        }
        /* Do NOT call real.click(): the main radio is above the viewport, so
           clicking it moves focus there and the browser scroll-jumps to it.
           Set checked + dispatch change instead — the theme reacts to change
           without moving focus, so nothing shifts. */
        if (real && !real.checked) {
          real.checked = true;
          real.dispatchEvent(new Event('input', { bubbles: true }));
          real.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      wrap.appendChild(btn);
    });

    /* Place the pills BEFORE the price (variant then price), per request. Fall
       back to before the add-to-cart button, then append. */
    var priceEl = drawer.querySelector('.product-sticky-bar__price');
    var atc = drawer.querySelector('.product-buy-buttons--primary');
    if (priceEl && priceEl.parentNode) priceEl.parentNode.insertBefore(wrap, priceEl);
    else if (atc) drawer.insertBefore(wrap, atc);
    else drawer.appendChild(wrap);
  }

  function syncActive() {
    var drawer = getDrawer();
    var optionBlock = getTargetOption();
    if (!drawer || !optionBlock) return;
    var wrap = drawer.querySelector('.' + WRAP_CLASS);
    if (!wrap) return;
    var val = currentValue(optionBlock);
    Array.prototype.forEach.call(wrap.querySelectorAll('.' + BTN_CLASS), function (b) {
      b.classList.toggle('is-active', b.getAttribute('data-value') === val);
    });
  }

  function ensurePresent() {
    var drawer = getDrawer();
    if (!drawer) return;
    if (!drawer.querySelector('.' + WRAP_CLASS)) build();
    else syncActive();
  }

  function init() {
    if (!getDrawer() || !getTargetOption()) return; /* not a product page with a drawer + options */
    injectStyle();
    build();

    /* Keep active state in sync when the real inputs change. */
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (t && t.classList && t.classList.contains('swatch--input')) syncActive();
    });

    /* The buy-buttons web component re-renders on variant change; re-inject
       our pills if they get dropped, and re-sync otherwise. Debounced. */
    var host = document.querySelector('product-buy-buttons-element') || document.body;
    var scheduled = false;
    try {
      var mo = new MutationObserver(function () {
        if (scheduled) return;
        scheduled = true;
        window.requestAnimationFrame(function () { scheduled = false; ensurePresent(); });
      });
      mo.observe(host, { childList: true, subtree: true });
    } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();


/* =====================================================================
 * 2) Delivery-ETA line under the main add-to-cart button (in-stock only)
 * ===================================================================== */
(function () {
  'use strict';

  var STYLE_ID = 'aqara-delivery-eta-style';
  var ETA_CLASS = 'aqara-delivery-eta';
  var ETA_TEXT = '預計 2–3 個工作天內送達';
  /* Out-of-stock / not-a-normal-purchase button labels (any locale we use). */
  var UNAVAILABLE_RE = /(售罄|售完|缺貨|補貨|預購|預售|到貨通知|sold\s*out|unavailable|out of stock|notify)/i;

  function injectStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var css = [
      '.' + ETA_CLASS + '{display:flex;align-items:center;justify-content:center;gap:7px;',
        'margin:12px 0 0;font-size:14px;line-height:1.4;color:#4e6e93;font-weight:500;}',
      '.' + ETA_CLASS + ' svg{width:18px;height:18px;flex:0 0 auto;}',
      '.' + ETA_CLASS + ' span{display:inline-block;}'
    ].join('');
    var s = document.createElement('style');
    s.id = STYLE_ID;
    s.textContent = css;
    document.head.appendChild(s);
  }

  /* The inline (main-form) add-to-cart button is a direct child of the
     buy-buttons element; the sticky-bar button lives inside .--drawer. */
  function inlineButton() {
    var root = document.querySelector('product-buy-buttons-element');
    if (!root) return null;
    var kids = Array.prototype.slice.call(root.children);
    for (var i = 0; i < kids.length; i++) {
      if (kids[i].classList && kids[i].classList.contains('product-buy-buttons--primary')) {
        return kids[i];
      }
    }
    return null;
  }

  function inStock(btn) {
    if (!btn) return false;
    if (btn.disabled) return false;
    if (btn.getAttribute('aria-disabled') === 'true') return false;
    if (UNAVAILABLE_RE.test(btn.textContent || '')) return false;
    return true;
  }

  function makeLine() {
    var el = document.createElement('div');
    el.className = ETA_CLASS;
    el.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
      '<path d="M2.5 6.5h11.5v9H2.5z" stroke="currentColor" stroke-width="1.5"/>' +
      '<path d="M14 9.5h3.8l3.2 3.2v2.8H14z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>' +
      '<circle cx="6.5" cy="17.5" r="1.9" fill="#fff" stroke="currentColor" stroke-width="1.5"/>' +
      '<circle cx="17.5" cy="17.5" r="1.9" fill="#fff" stroke="currentColor" stroke-width="1.5"/>' +
      '</svg><span></span>';
    el.querySelector('span').textContent = ETA_TEXT;
    return el;
  }

  function apply() {
    var btn = inlineButton();
    if (!btn) return;
    injectStyle();
    var existing = btn.parentNode.querySelector(':scope > .' + ETA_CLASS)
                || document.querySelector('.' + ETA_CLASS);
    if (inStock(btn)) {
      if (!existing) {
        btn.parentNode.insertBefore(makeLine(), btn.nextSibling);
      }
    } else if (existing) {
      existing.parentNode.removeChild(existing);
    }
  }

  function init() {
    if (!inlineButton()) return;
    apply();

    /* Re-evaluate when the variant changes or the button state updates. */
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (t && t.classList && t.classList.contains('swatch--input')) {
        window.requestAnimationFrame(apply);
      }
    });

    var host = document.querySelector('product-buy-buttons-element') || document.body;
    var scheduled = false;
    try {
      var mo = new MutationObserver(function () {
        if (scheduled) return;
        scheduled = true;
        window.requestAnimationFrame(function () { scheduled = false; apply(); });
      });
      mo.observe(host, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled']
      });
    } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
